import { Injectable, Inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  AddressResponse,
  AddressRetrieveResponse,
  AddressSearchDrillDownResponse,
  AddressSearchResponse,
} from '../client/src';
import { HttpStatusCode } from '@angular/common/http';
import { AddressesStoreActions } from '../actions';
import { AddressesStoreFacade } from '../facade';
import { AddressesService } from '../services/addresses.service';
import { mapAddressResponse } from '../util/convert-address';
import { isApiError } from '@nx-ng/util';
import { ApiError } from '@modules/checkout-store';
import { LoggingService } from '@nx-ng/types';
import { LOGGER } from '@modules/core';

@Injectable()
export class AddressesStoreEffects {
  constructor(
    private readonly actions$: Actions,
    private addressesService: AddressesService,
    private facade: AddressesStoreFacade,
    @Inject(LOGGER) private logger: LoggingService
  ) {}

  search$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddressesStoreActions.addressAPI_search),
      withLatestFrom(this.facade.searchSession$),
      switchMap(
        ([action, session]: [
          { searchText: string; requester: string } & Action,
          string | undefined
        ]) => {
          const { searchText, requester } = action;
          return this.addressesService.search(searchText, session).pipe(
            map((value: AddressSearchResponse) => {
              if (value) {
                return AddressesStoreActions.addressAPI_searchSuccess({
                  searchResponse: value,
                  requester,
                });
              }
              return AddressesStoreActions.addressAPI_searchFailure({
                error: 'empty response',
                requester,
                retryAction: action,
              });
            }),
            catchError((error) => {
              const apiError: ApiError | string =
                error?.response?.parsedBody || error;

              this.logger.logApiError(
                {
                  apiError,
                },
                error
              );

              if (isApiError(error) && error.code === HttpStatusCode.NotFound) {
                return of(
                  AddressesStoreActions.addressAPI_searchSuccess({
                    searchResponse: {},
                    requester,
                  })
                );
              }

              return of(
                AddressesStoreActions.addressAPI_searchFailure({
                  error: apiError,
                  retryAction: action,
                  requester: 'other search error',
                })
              );
            })
          );
        }
      )
    )
  );

  drilldown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddressesStoreActions.addressAPI_drillDown),
      switchMap((action) => {
        const { requester, id: value } = action;
        return this.addressesService.drillDown(value).pipe(
          map((value: AddressSearchDrillDownResponse) => {
            if (value) {
              return AddressesStoreActions.addressAPI_drillDownSuccess({
                searchResponse: value,
                requester,
              });
            }
            return AddressesStoreActions.addressAPI_drillDownFailure({
              error: 'empty response',
              requester,
              retryAction: action,
            });
          }),
          catchError((error) => {
            const apiError: ApiError | string =
              error?.response?.parsedBody || error;

            this.logger.logApiError(
              {
                apiError,
              },
              error
            );

            if (isApiError(error) && error.code === HttpStatusCode.NotFound) {
              return of(
                AddressesStoreActions.addressAPI_drillDownSuccess({
                  searchResponse: {},
                  requester,
                })
              );
            }
            return of(
              AddressesStoreActions.addressAPI_drillDownFailure({
                error: apiError,
                requester: 'other drilldown error',
                retryAction: action,
              })
            );
          })
        );
      })
    )
  );

  retrieve$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddressesStoreActions.addressAPI_retrieve),
      switchMap((action) => {
        const { requester, id } = action;
        return this.addressesService.retrieveAddress(id).pipe(
          map((value: AddressRetrieveResponse) => {
            if (value) {
              const address = mapAddressResponse(value?.address);
              if (address) {
                return AddressesStoreActions.addressAPI_retrieveSuccess({
                  address,
                  requester,
                });
              }
            }
            return AddressesStoreActions.addressAPI_retrieveFailure({
              error: 'empty response',
              requester,
              retryAction: action,
            });
          }),
          catchError((error) => {
            const apiError: ApiError | string =
              error?.response?.parsedBody || error;

            this.logger.logApiError(
              {
                apiError,
              },
              error
            );

            return of(
              AddressesStoreActions.addressAPI_retrieveFailure({
                error: apiError,
                requester: 'other retrieve error',
                retryAction: action,
              })
            );
          })
        );
      })
    )
  );
}

interface Dummy {
  a: number;
  b: number;
}

const test = (a: { a: number; b: number }) => {
  a.a;
};
